import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { AppIcon } from "@/components/AppIcon";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoCluster } from "@/components/LogoBadges";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1627920769842-6887c6df05ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxzbWFydCUyMGFncmljdWx0dXJlJTIwc2Vuc29yfGVufDB8fHx8MTc4MDU1NjA3Mnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
    // Reset email/password defaults to empty (no demo hint after release)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const { login: branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.from?.pathname || "/";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="Smart agriculture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-stone-900/80 via-stone-900/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <AppIcon url={branding.icon_url} size={40} className="bg-white/15 backdrop-blur" />
            <div className="font-display font-bold text-xl">{branding.app_name}</div>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">{branding.extra_info || "Selamat datang"}</div>
            <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
              {branding.description || "Monitoring lahan & kendali irigasi real-time dari mana saja."}
            </h2>
            <div className="mt-8 flex items-center gap-2">
              <LogoCluster />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <AppIcon url={branding.icon_url} size={40} />
            <div className="font-display font-bold text-xl">{branding.app_name}</div>
          </div>
          <div className="overline text-primary mb-3">Selamat datang</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Masuk ke akun Anda</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Belum punya akun?{" "}
            <Link to="/register" className="text-primary underline-offset-4 hover:underline" data-testid={LOGIN.registerLink}>
              Daftar sekarang
            </Link>
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={LOGIN.emailInput}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={LOGIN.passwordInput}
                required
                autoComplete="current-password"
              />
            </div>
            {error && (
              <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={submitting} data-testid={LOGIN.submitButton}>
              {submitting ? "Masuk…" : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
